import { Box, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { audit, metrics, type TenantMetrics } from '@/api/tenant';
import { DataTable } from '@/components/DataTable';
import { MetricStat } from '@/components/MetricStat';
import { StatusPill, auditStatusTone } from '@/components/StatusPill';
import { TrendChart, type TrendPoint } from '@/components/TrendChart';
import { PageShell } from '@/layout/PageShell';
import { dailyBuckets, resolveWindow, type WindowKey } from '@/utils/timeWindow';
import { relativeTime, utcStamp } from '@/utils/format';
import type { AbdmEventLog } from '@/types/backend';

/**
 * Tenant metrics — closes the read-side half of gap #5. The /api/abdm/
 * tenant-metrics endpoint already returns milestone counts + recent
 * events; we add a 14-day trend by paging through the audit events
 * endpoint and bucketing them client-side. Heavy windows would belong
 * on a dedicated rollup-read endpoint, but for "last 14 days" the
 * 50-row pages add up to a few requests.
 *
 * Window toggle drives a single SWR key per window; the API returns
 * `source: "live" | "rollup+live-tail"` from PR-8 and we surface that
 * as a small pill so operators know they're on cached or live data.
 */

const WINDOWS: WindowKey[] = ['24h', '7d', '14d', '30d'];

export function MetricsPage() {
  const theme = useTheme();
  const [windowKey, setWindowKey] = useState<WindowKey>('7d');
  const win = useMemo(() => resolveWindow(windowKey), [windowKey]);

  const { data, error, isLoading } = useSWR<TenantMetrics>(
    ['tenant-metrics', win.fromIso, win.toIso],
    () => metrics.read({ from: win.fromIso, to: win.toIso }),
  );

  // Trend: we already have `recentEvents` on `data` but it's capped at
  // 50. Pull a bigger page (size=500) just for bucketing. SWR dedupes
  // when the window doesn't change.
  const { data: trendPage } = useSWR(
    ['tenant-metrics-trend', win.fromIso, win.toIso, win.days],
    () => audit.events({ from: win.fromIso, to: win.toIso, page: 0, size: 500 }),
  );

  const trend = useMemo<TrendPoint[]>(() => {
    if (!trendPage) return [];
    const buckets = dailyBuckets(win.fromIso, win.days);
    const map = new Map<string, TrendPoint>(buckets.map((b) => [b.date, { date: b.date, pass: 0, fail: 0 }]));
    for (const e of trendPage.content) {
      const day = e.createdAt.slice(0, 10);
      const point = map.get(day);
      if (!point) continue;
      if (e.status === 'PASS') point.pass += 1;
      else if (e.status === 'FAIL') point.fail += 1;
    }
    return [...map.values()];
  }, [trendPage, win.fromIso, win.days]);

  const passRate = useMemo(() => {
    if (!data) return null;
    const total = (data.totalSuccess ?? 0) + (data.totalFailure ?? 0) + (data.totalTimeout ?? 0);
    if (total === 0) return null;
    return ((data.totalSuccess ?? 0) / total) * 100;
  }, [data]);

  return (
    <PageShell
      kicker="TENANT · METRICS"
      title="Metrics"
      subtitle="Pass/fail counts, trend, and the most recent events for your tenant. Long windows read from the daily rollup; short windows read live."
      width="wide"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Tabs
          value={windowKey}
          onChange={(_, v: WindowKey) => setWindowKey(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500, fontSize: 13 },
          }}
        >
          {WINDOWS.map((w) => (
            <Tab key={w} value={w} label={resolveWindow(w).label} />
          ))}
        </Tabs>
        {data?.source && (
          <StatusPill tone={data.source === 'live' ? 'info' : 'success'} dot>
            {data.source === 'live' ? 'Live data' : 'Rollup + live tail'}
          </StatusPill>
        )}
      </Stack>

      {/* Stat strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <MetricStat
          label="Pass rate"
          value={passRate === null ? '—' : `${passRate.toFixed(1)}%`}
          tone={passRate === null ? 'neutral' : passRate >= 99 ? 'success' : passRate >= 95 ? 'warning' : 'danger'}
          hint={data ? `${data.totalSuccess.toLocaleString()} success / ${data.totalFailure.toLocaleString()} fail` : undefined}
        />
        <MetricStat label="Successful calls"  value={data?.totalSuccess.toLocaleString() ?? '…'} tone="success" />
        <MetricStat label="Failed calls"      value={data?.totalFailure.toLocaleString() ?? '…'} tone={(data?.totalFailure ?? 0) > 0 ? 'danger' : 'success'} />
        <MetricStat label="Timeouts"          value={data?.totalTimeout.toLocaleString() ?? '…'} tone={(data?.totalTimeout ?? 0) > 0 ? 'warning' : 'success'} />
      </Box>

      {/* Milestone bars */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
          By milestone
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {(['M1', 'M2', 'M3'] as const).map((m) => {
            const c = data?.milestones?.[m];
            const passN = c?.pass ?? 0;
            const failN = c?.fail ?? 0;
            const total = passN + failN;
            const passPct = total > 0 ? (passN / total) * 100 : 0;
            return (
              <Box
                key={m}
                sx={{
                  bgcolor: theme.palette.surface.card,
                  border: `1px solid ${theme.palette.border.subtle}`,
                  borderRadius: theme.radius.md + 'px',
                  p: 2.5,
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="overline" color="text.tertiary">{m}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {total.toLocaleString()}
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 8, borderRadius: theme.radius.pill + 'px', bgcolor: theme.palette.surface.sunken, overflow: 'hidden', display: 'flex' }}>
                    <Box sx={{ width: `${passPct}%`, bgcolor: theme.palette.status.success, transition: `width ${theme.motion.durations.medium}ms ${theme.motion.easings.standard}` }} />
                    <Box sx={{ width: `${100 - passPct}%`, bgcolor: theme.palette.status.danger, transition: `width ${theme.motion.durations.medium}ms ${theme.motion.easings.standard}` }} />
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" sx={{ color: theme.palette.status.success, fontWeight: 600 }}>
                      {passN.toLocaleString()} pass
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.status.danger, fontWeight: 600 }}>
                      {failN.toLocaleString()} fail
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Trend */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
          Trend ({win.label.toLowerCase()})
        </Typography>
        <Box
          sx={{
            bgcolor: theme.palette.surface.card,
            border: `1px solid ${theme.palette.border.subtle}`,
            borderRadius: theme.radius.md + 'px',
            p: 2,
          }}
        >
          <TrendChart data={trend} />
        </Box>
      </Box>

      {/* Recent events */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>
          Recent events ({(data?.recentEvents ?? []).length})
        </Typography>
        <DataTable<AbdmEventLog>
          rows={data?.recentEvents}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          error={error ? { message: (error as Error).message } : null}
          empty={{ title: 'No events in this window.', description: 'Try a longer window.' }}
          columns={[
            {
              header: 'When',
              cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
              width: 130,
            },
            {
              header: 'Milestone',
              cell: (r) => <Typography variant="caption" sx={{ fontWeight: 600 }}>{r.milestone}</Typography>,
              width: 100,
            },
            {
              header: 'Event',
              cell: (r) => (
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={500}>{r.eventType}</Typography>
                  {r.stepName && <Typography variant="caption" color="text.tertiary">{r.stepName}</Typography>}
                </Stack>
              ),
            },
            {
              header: 'Status',
              cell: (r) => <StatusPill tone={auditStatusTone(r.status)}>{r.status}</StatusPill>,
              width: 100,
            },
            {
              header: 'Message',
              cell: (r) => (
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {r.message ?? '—'}
                </Typography>
              ),
            },
          ]}
        />
      </Box>
    </PageShell>
  );
}
